import React, { FC, SyntheticEvent, useCallback, useMemo } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';


import { useTranslation } from 'Modules/core/i18n';
import { Box, Button2, CloseModalButton, Typography } from 'Modules/ui3';
import { useAction } from 'Modules/core/redux';
import { messagingActions, useModalContext } from 'Modules/core/messaging';
import { validators } from 'Modules/core/forms';
import { CheckboxField } from 'Modules/business/forms';

import { FeedbackFormProps, FormValues, SelectedTabComponentProps } from './types';
import { CommonBlock } from './CommonBlock';
import { componentByRoleMap } from './CommonBlock/utils';
import styles from '../ui/index.module.scss';
import { feedbackActions, useFeedbackBusy } from '../../state/feedback';


const SelectedTabComponent: FC<SelectedTabComponentProps> = ({ role, t }) => {
    const Component = useMemo(
        () => componentByRoleMap[role],
        [role],
    );
    return Component && (
        <Box mt={6} mb={8}>
            <Component t={t} />
        </Box>
    );
};

export const FeedbackForm: FC<FeedbackFormProps> = ({ formValues }) => {
    const { t } = useTranslation('feedbackForm');
    const { modal } = useModalContext();
    const busy = useFeedbackBusy();

    const submit = useAction((formValues: FormValues) => feedbackActions.submitForm({
        formValues,
        modalId: modal.id,
    }), [modal.id]);

    const showApprovalModal = useAction(() => messagingActions.showModal({
        type: 'PersonalDataModal',
        closeable: false,
    }));

    const showApproval = useCallback((e: SyntheticEvent<HTMLElement>) => {
        e.preventDefault();
        showApprovalModal();
    }, [showApprovalModal]);

    return (
        <FinalForm
            name='feedbackForm'
            subscription={{ values: true, dirtyFields: true }}
            initialValues={formValues}
            onSubmit={submit}
            keepDirtyOnReinitialize
        >
            {({ handleSubmit, values }) => (
                <Box
                    component='form'
                    onSubmit={handleSubmit}
                    height='100%'
                    maxWidth='560px'
                    boxSizing='border-box'
                    py={8}
                >
                    <CloseModalButton onClick={modal.hide} size='small' />
                    <Box pl={8} maxHeight='100%' className={styles.scrollable}>
                        <Box pr={7}>
                            <CommonBlock t={t} price={values.price} role={values.role} />
                            <SelectedTabComponent t={t} role={values.role} />
                            <Field
                                name='approval'
                                type='checkbox'
                                component={CheckboxField}
                                typographyProps={{
                                    variant: 'regularCaption1',
                                    color: 'grayscale.shade95',
                                }}
                                label={(
                                    <>
                                        {t('approval')}
                                        <Typography
                                            component='a'
                                            variant='regularCaption1'
                                            onClick={showApproval}
                                        >
                                            {t('personalData')}
                                        </Typography>
                                    </>
                                )}
                                validate={validators.required}
                            />
                            <Button2
                                type='submit'
                                variantType='brand'
                                fullWidth
                                sx={{ mt: 2 }}
                                loading={busy}
                            >
                                {t('submit')}
                            </Button2>
                        </Box>
                    </Box>
                </Box>
            )}
        </FinalForm>
    );
};
